import i18n from "i18next";

import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { buildQueryParams } from "../utils/utils";
import { useAuthContext } from "./use-auth-context.hook";

export type NotificationSeverity = 'info' | 'success' | 'warning' | 'error';

export type NotificationCategory =
  | 'leads'
  | 'ardi'
  | 'catalogue'
  | 'marketing'
  | 'channels'
  | 'billing'
  | 'support'
  | 'sales'
  | 'platform'
  | 'other';

/**
 * A notification as the API renders it: the stored facts plus the words, in
 * the language asked for. The copy lives in one catalog on the API — the same
 * one that writes the email and the Slack message — so the bell never has to
 * know a type to show it properly.
 */
export interface NotificationItem {
  _id: string;
  audience: 'vendor' | 'superadmin';
  vendorId?: string | null;
  type: string;
  data: Record<string, any>;
  severity: NotificationSeverity;
  readAt: string | null;
  createdAt: string;
  /** Whether the signed-in person has read it. */
  read?: boolean;
  title?: string;
  body?: string;
  detail?: string | null;
  route?: string | null;
  category?: NotificationCategory;
}

export interface NotificationsResponse {
  data: NotificationItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface NotificationPreferenceRow {
  type: string;
  category: NotificationCategory;
  label: string;
  about: string;
  inApp: boolean;
  email: boolean;
  lockedInApp: boolean;
  emailedSeparately: boolean;
}

export interface NotificationPreferences {
  categories: NotificationCategory[];
  types: NotificationPreferenceRow[];
  /** False when the session has no person to store choices for. */
  personal: boolean;
}

/** The app's language as the API knows it (en/fr/es/ar). */
export const apiLang = () => {
  const lang = (i18n.language || 'en').slice(0, 2);
  return ['en', 'fr', 'es', 'ar'].includes(lang) ? lang : 'en';
};

export const isUnread = (item: NotificationItem) => (item.read === undefined ? !item.readAt : !item.read);

export const useNotificationsApi = () => {
  const { sendAuthGuardedRequest } = useAuthContext();

  const findAll = async (params?: { page?: number; limit?: number; unreadOnly?: boolean; category?: string }): Promise<NotificationsResponse> => {
    const queryParams = [{ key: "lang", value: apiLang() }];
    if (params?.page) queryParams.push({ key: "page", value: params.page.toString() });
    if (params?.limit) queryParams.push({ key: "limit", value: params.limit.toString() });
    if (params?.unreadOnly) queryParams.push({ key: "unreadOnly", value: "true" });
    if (params?.category) queryParams.push({ key: "category", value: params.category });
    const queryString = buildQueryParams(queryParams);
    return await sendAuthGuardedRequest(ApiMethod.GET, routes.notifications.findAll + queryString) as NotificationsResponse;
  };

  const getUnreadCount = async (): Promise<{ count: number }> => {
    return await sendAuthGuardedRequest(ApiMethod.GET, routes.notifications.unreadCount) as { count: number };
  };

  const getSummary = async (): Promise<{ unread: number; byCategory: Record<string, number> }> => {
    return await sendAuthGuardedRequest(ApiMethod.GET, routes.notifications.summary) as { unread: number; byCategory: Record<string, number> };
  };

  const markRead = async (id: string): Promise<NotificationItem> => {
    return await sendAuthGuardedRequest(ApiMethod.PATCH, routes.notifications.markRead(id)) as NotificationItem;
  };

  const markAllRead = async (category?: string): Promise<{ modified: number }> => {
    return await sendAuthGuardedRequest(ApiMethod.PATCH, routes.notifications.markAllRead, category ? { category } : {}) as { modified: number };
  };

  const getPreferences = async (): Promise<NotificationPreferences> => {
    return await sendAuthGuardedRequest(ApiMethod.GET, `${routes.notifications.preferences}?lang=${apiLang()}`) as NotificationPreferences;
  };

  const updatePreferences = async (changes: { type: string; inApp?: boolean; email?: boolean }[]): Promise<NotificationPreferences> => {
    return await sendAuthGuardedRequest(ApiMethod.PUT, `${routes.notifications.preferences}?lang=${apiLang()}`, { changes }) as NotificationPreferences;
  };

  return { findAll, getUnreadCount, getSummary, markRead, markAllRead, getPreferences, updatePreferences };
};
